import React, { useState } from "react";
import { Card } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { ApplicationApi } from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";
import { AppState } from "../../../utils/types";
import { parseApplicationSettings, parseApplicationSettingsValues, parseStringToList } from "../../../utils/utils";
import WoltDriveAdvancedForm from "./WoltDriveAdvancedForm";
import WoltDriveBasicForm from "./WoltDriveBasicForm";

interface Props {
	appState: AppState;
	handleUpdateApp: (data: ApplicationApi) => void;
}

const WoltDriveFormModalCard = ({ appState, handleUpdateApp }: Props) => {
	const { t } = useTranslation();
	const [updatedConfig, setUpdatedConfig] = useState<Record<string, any> | undefined>(appState.config);
	const form = useForm<ApplicationApi>({
		criteriaMode: "all",
		defaultValues: {
			...appState.app,
			settings: {
				...appState.app.settings,
				...parseApplicationSettingsValues(appState.app.settings || {}),
				notification_terminal: parseStringToList(
					appState.app.settings,
					updatedConfig || {},
					"notification_terminal",
					"notification_terminals"
				)?.map((terminal) => ({
					label: terminal?.name,
					id: terminal?.id,
				})),
			},
		},
	});
	const [loading, setLoading] = useState<boolean>(false);
	const { handleSubmit, formState, reset, setError, watch } = form;
	const { addFlash, addSuccessFlash } = useFlash();

	const onSubmit = handleSubmit(async (data: ApplicationApi) => {
		setLoading(true);
		const params: Record<string, any> = { include: "settings" };

		const settings = parseApplicationSettings(data.settings);
		const newData = {
			name: data.name,
			id: appState.app.id,
			settings,
		};

		try {
			const res = await api.organization().updateApp(newData, params);
			handleUpdateApp(res);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			reset(watch());
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const handleUpdateConfig = (field: string, obj?: any) => {
		let newObj;
		if (Array.isArray(obj)) {
			newObj = obj.map((item: Record<string, any>) => {
				return {
					name: item.name,
					id: item.id,
				};
			});
		} else {
			newObj = obj !== undefined ? { ...obj } : obj;
		}

		const copiedConfig = { ...updatedConfig };
		if (field === "notification_terminals") {
			copiedConfig.notification_terminals = newObj;
		}

		setUpdatedConfig(copiedConfig);
	};

	return (
		<Card>
			<Card.Body>
				<FormDirty formState={formState} onSubmit={onSubmit} className="hotellion-app-form">
					<WoltDriveBasicForm form={form} />
					<WoltDriveAdvancedForm
						form={form}
						appState={appState}
						handleUpdateConfig={handleUpdateConfig}
						updatedConfig={updatedConfig}
					/>
					<ButtonLoading loading={loading} variant="add" onClick={onSubmit}>
						{t("common.action.save", { ns: "lib" })}
					</ButtonLoading>
				</FormDirty>
			</Card.Body>
		</Card>
	);
};

export default WoltDriveFormModalCard;
